import { FC } from 'react'
import { useIntl } from 'react-intl'
import { Link, useNavigate } from 'react-router-dom'
import { LayoutDashboard } from 'lucide-react'
import { useFeature } from '../../../Utils/featureFlagEngine'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import './NotFound.scss'

interface NotFoundProps {
  /** Show the Netbaan logo above the content (for the full-screen /error/404 route). */
  showLogo?: boolean
}

/**
 * Large-404 "page not found" screen.
 *
 * Rendered in two places:
 *  - as the authenticated catch-all inside MasterLayout (keeps the app shell), and
 *  - full-screen at /error/404 (pass `showLogo`).
 *
 * Colors come from Metronic `--bs-*` CSS variables, so it follows light/dark automatically.
 */
const NotFound: FC<NotFoundProps> = ({ showLogo = false }) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { getMainRoute } = useFeature()
  const home = getMainRoute() || '/'

  const goBack = () => {
    // react-router records a per-entry index in history.state. When it's > 0
    // there's an in-app entry to pop back to (client-side, no reload). When
    // it's 0 (the 404 is the first entry — e.g. the user opened the bad URL
    // directly), navigate(-1) would leave the SPA and trigger a full reload,
    // so fall back to a client-side navigation home instead.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) {
      navigate(-1)
    } else {
      navigate(home, { replace: true })
    }
  }

  return (
    <div className={`netbaan-404${showLogo ? ' netbaan-404--full' : ''}`}>
      {showLogo && (
        <a href={home} className='netbaan-404__logo'>
          <img alt='Logo' src={toAbsoluteUrl('/splashLogo.png')} />
        </a>
      )}

      <div className='netbaan-404__num' role='img' aria-label='404'>
        <span className='netbaan-404__digit'>4</span>
        <span className='netbaan-404__zero'>
          <svg viewBox='0 0 100 100' fill='none' aria-hidden='true'>
            <circle className='netbaan-404__z-fill' cx='50' cy='50' r='34' />
            <circle className='netbaan-404__z-ring' cx='50' cy='50' r='41' strokeWidth={4} />
            <circle className='netbaan-404__z-ring-inner' cx='50' cy='50' r='30' strokeWidth={3} />
            <g strokeWidth={4} strokeLinecap='round'>
              <line className='netbaan-404__z-tick' x1='50' y1='5' x2='50' y2='16' />
              <line className='netbaan-404__z-tick' x1='50' y1='84' x2='50' y2='95' />
              <line className='netbaan-404__z-tick' x1='5' y1='50' x2='16' y2='50' />
              <line className='netbaan-404__z-tick' x1='84' y1='50' x2='95' y2='50' />
            </g>
            <circle className='netbaan-404__z-dot' cx='50' cy='50' r='5' />
          </svg>
        </span>
        <span className='netbaan-404__digit'>4</span>
      </div>

      <h1 className='netbaan-404__title'>
        {intl.formatMessage({ id: "We couldn't find that page" })}
      </h1>
      <p className='netbaan-404__sub'>
        {intl.formatMessage({
          id: "The address you followed doesn't lead anywhere in Netbaan. The link may be outdated, mistyped, or the page may have been removed.",
        })}
      </p>

      <div className='netbaan-404__actions'>
        <Link to={home} className='btn btn-primary d-inline-flex align-items-center gap-2'>
          <LayoutDashboard size={16} />
          {intl.formatMessage({ id: 'Go to Dashboard' })}
        </Link>
        <button
          type='button'
          onClick={goBack}
          className='btn btn-light d-inline-flex align-items-center gap-2'
        >
          {intl.formatMessage({ id: 'Go back' })}
        </button>
      </div>
    </div>
  )
}

export default NotFound
